import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Webhook, Plus, Trash2, ToggleLeft, ToggleRight, RefreshCw,
  CheckCircle2, XCircle, Clock, Copy, Eye, EyeOff, ChevronRight,
  Send, RotateCcw, AlertCircle, ExternalLink
} from "lucide-react";

// ─── helpers ─────────────────────────────────────────────────────────────────
function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
      timeZone: "Asia/Kolkata",
    });
  } catch { return iso; }
}

function StatusBadge({ status }) {
  const map = {
    delivered: { cls: "bg-emerald-100 text-emerald-700", icon: <CheckCircle2 className="w-3 h-3" />, label: "Delivered" },
    pending:   { cls: "bg-amber-100 text-amber-700",   icon: <Clock className="w-3 h-3" />,        label: "Pending"   },
    failed:    { cls: "bg-red-100 text-red-700",        icon: <XCircle className="w-3 h-3" />,      label: "Failed"    },
    abandoned: { cls: "bg-slate-100 text-slate-600",    icon: <AlertCircle className="w-3 h-3" />,  label: "Abandoned" },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
      {s.icon}{s.label}
    </span>
  );
}

// ─── Add / Edit modal ────────────────────────────────────────────────────────
function WebhookModal({ hook, onClose, onSave }) {
  const editing = Boolean(hook?.id);
  const [form, setForm] = useState({ name: hook?.name || "", url: hook?.url || "", active: hook?.active ?? true });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.url.startsWith("http")) return toast.error("URL must start with http:// or https://");
    setSaving(true);
    try {
      let result;
      if (editing) {
        const r = await api.put(`/webhooks/${hook.id}`, form);
        result = r.data;
        toast.success("Webhook updated");
      } else {
        const r = await api.post("/webhooks", form);
        result = r.data;
        toast.success("Webhook created");
      }
      onSave(result, !editing);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save webhook");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="webhook-modal">
        <div className="px-6 py-5 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-800">{editing ? "Edit Webhook" : "Add Webhook"}</h2>
          <p className="text-sm text-slate-500 mt-0.5">Receives a POST request when a payment is completed</p>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
            <input
              data-testid="webhook-name-input"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Google Sheets Sync"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Endpoint URL</label>
            <input
              data-testid="webhook-url-input"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://script.google.com/macros/s/..."
              value={form.url}
              onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
            />
            <p className="mt-1 text-xs text-slate-400">Paste your Google Apps Script webapp URL or any webhook endpoint</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, active: !f.active }))}
              className={`relative w-11 h-6 rounded-full transition-colors ${form.active ? "bg-blue-600" : "bg-slate-300"}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.active ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
            <span className="text-sm text-slate-600">{form.active ? "Active — will receive events" : "Inactive — events paused"}</span>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              data-testid="webhook-save-btn"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? "Saving…" : editing ? "Save Changes" : "Create Webhook"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Secret reveal modal ─────────────────────────────────────────────────────
function SecretModal({ webhookId, webhookName, onClose }) {
  const [secret, setSecret] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/webhooks/${webhookId}/secret`)
      .then(r => setSecret(r.data.secret))
      .catch(() => toast.error("Could not fetch secret"))
      .finally(() => setLoading(false));
  }, [webhookId]);

  const copy = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Secret copied to clipboard");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="secret-modal">
        <div className="px-6 py-5 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-800">Signing Secret</h2>
          <p className="text-sm text-slate-500 mt-0.5">{webhookName}</p>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-slate-600 mb-3">
            Use this secret in your endpoint to verify that requests are genuinely from GDIOT PAY.
            The header <code className="bg-slate-100 px-1 rounded text-xs">X-GDIOT-Signature</code> contains
            an HMAC-SHA256 hash of the payload signed with this key.
          </p>
          {loading ? (
            <div className="h-10 bg-slate-100 rounded-lg animate-pulse" />
          ) : (
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono break-all text-slate-700">
                {secret}
              </code>
              <button onClick={copy} className="shrink-0 p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Copy">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          )}
          {copied && <p className="text-xs text-emerald-600 mt-1">Copied!</p>}
        </div>
        <div className="px-6 pb-5">
          <button onClick={onClose} className="w-full px-4 py-2 bg-slate-100 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-200 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delivery logs drawer ────────────────────────────────────────────────────
function LogsDrawer({ hook, onClose, onRetry }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/webhooks/${hook.id}/logs`)
      .then(r => setLogs(r.data))
      .catch(() => toast.error("Failed to load logs"))
      .finally(() => setLoading(false));
  }, [hook.id]);

  useEffect(() => { load(); }, [load]);

  const retry = async () => {
    try {
      const r = await api.post(`/webhooks/${hook.id}/retry-failed`);
      toast.success(`Re-queued ${r.data.requeued} failed deliveries`);
      onRetry?.();
    } catch { toast.error("Retry failed"); }
  };

  const resendAll = async () => {
    if (!window.confirm("Re-queue ALL past deliveries (including ones marked as delivered with errors)?\n\nUse this after fixing your endpoint.")) return;
    try {
      const r = await api.post(`/webhooks/${hook.id}/resend-all`);
      toast.success(`Re-queued ${r.data.requeued} deliveries for resend`);
      load();
      onRetry?.();
    } catch { toast.error("Resend all failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white h-full w-full max-w-xl shadow-2xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="logs-drawer"
      >
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-800">Delivery Logs</h2>
            <p className="text-xs text-slate-500 mt-0.5 font-mono truncate max-w-xs">{hook.url}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={retry} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors" data-testid="retry-failed-btn">
              <RotateCcw className="w-3 h-3" />Retry Failed
            </button>
            <button onClick={resendAll} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors" data-testid="resend-all-btn">
              <RotateCcw className="w-3 h-3" />Resend All
            </button>
            <button onClick={onClose} className="ml-1 p-2 rounded-lg hover:bg-slate-100 text-slate-400">✕</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {loading && (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
              ))}
            </div>
          )}
          {!loading && logs.length === 0 && (
            <div className="flex flex-col items-center justify-center h-48 text-slate-400">
              <Clock className="w-10 h-10 mb-2 opacity-40" />
              <p className="text-sm">No deliveries yet</p>
              <p className="text-xs mt-1">Logs appear when payment.completed fires</p>
            </div>
          )}
          {!loading && logs.map(log => (
            <div key={log.id} className="mb-3 border border-slate-200 rounded-xl overflow-hidden" data-testid="log-entry">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50">
                <div className="flex items-center gap-2">
                  <StatusBadge status={log.success ? "delivered" : "failed"} />
                  <span className="text-xs text-slate-500">Attempt #{log.attempt_number}</span>
                </div>
                <div className="flex items-center gap-3">
                  {log.response_status_code && (
                    <span className={`text-xs font-mono font-semibold ${log.success ? "text-emerald-600" : "text-red-600"}`}>
                      HTTP {log.response_status_code}
                    </span>
                  )}
                  {log.duration_ms !== undefined && (
                    <span className="text-xs text-slate-400">{log.duration_ms}ms</span>
                  )}
                  <span className="text-xs text-slate-400">{fmtTime(log.sent_at)}</span>
                </div>
              </div>
              {log.response_body && (
                <div className="px-4 py-2 bg-white">
                  <code className="text-xs text-slate-500 break-all font-mono">{log.response_body}</code>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Webhook row card ────────────────────────────────────────────────────────
function WebhookCard({ hook, onToggle, onEdit, onDelete, onTestFire, onViewLogs, onViewSecret }) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const testFire = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post(`/webhooks/${hook.id}/test`);
      setTestResult(r.data);
      if (r.data.ok) toast.success(`Test delivered — HTTP ${r.data.status_code}`);
      else toast.error(`Test failed — ${r.data.error || `HTTP ${r.data.status_code}`}`);
    } catch { toast.error("Test fire failed"); }
    finally { setTesting(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 hover:border-slate-300 transition-all shadow-sm" data-testid="webhook-card">
      <div className="px-5 py-4 flex items-start gap-4">
        {/* Icon */}
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${hook.active ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-400"}`}>
          <Webhook className="w-5 h-5" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 text-sm" data-testid="webhook-name">{hook.name}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${hook.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
              {hook.active ? "Active" : "Inactive"}
            </span>
          </div>
          <p className="text-xs text-slate-400 font-mono mt-0.5 truncate max-w-sm" data-testid="webhook-url">
            {hook.url}
          </p>
          <p className="text-xs text-slate-400 mt-1">Event: <span className="font-medium text-slate-600">payment.completed</span></p>
          {testResult && (
            <div className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${testResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
              {testResult.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
              {testResult.ok ? `Test OK — HTTP ${testResult.status_code}` : `Test failed — ${testResult.error || `HTTP ${testResult.status_code}`}`}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => onToggle(hook)}
            data-testid="webhook-toggle-btn"
            title={hook.active ? "Deactivate" : "Activate"}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
          >
            {hook.active ? <ToggleRight className="w-5 h-5 text-blue-600" /> : <ToggleLeft className="w-5 h-5" />}
          </button>
          <button
            onClick={() => onViewSecret(hook)}
            data-testid="webhook-secret-btn"
            title="View signing secret"
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={testFire}
            disabled={testing}
            data-testid="webhook-test-btn"
            title="Send test payload"
            className="p-2 rounded-lg hover:bg-blue-50 text-slate-500 hover:text-blue-600 transition-colors disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewLogs(hook)}
            data-testid="webhook-logs-btn"
            title="Delivery logs"
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(hook)}
            data-testid="webhook-delete-btn"
            title="Delete webhook"
            className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export default function Webhooks() {
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editHook, setEditHook] = useState(null);
  const [logsHook, setLogsHook] = useState(null);
  const [secretHook, setSecretHook] = useState(null);
  const [newSecret, setNewSecret] = useState(null);   // shown after creation

  const loadHooks = useCallback(() => {
    setLoading(true);
    api.get("/webhooks")
      .then(r => setHooks(r.data))
      .catch(() => toast.error("Failed to load webhooks"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadHooks(); }, [loadHooks]);

  const handleSave = (result, isNew) => {
    if (isNew && result.secret) {
      setNewSecret({ id: result.id, name: result.name, secret: result.secret });
    }
    loadHooks();
    setShowModal(false);
    setEditHook(null);
  };

  const handleToggle = async (hook) => {
    try {
      const r = await api.put(`/webhooks/${hook.id}`, { active: !hook.active });
      setHooks(h => h.map(x => x.id === hook.id ? r.data : x));
      toast.success(r.data.active ? "Webhook activated" : "Webhook deactivated");
    } catch { toast.error("Toggle failed"); }
  };

  const handleDelete = async (hook) => {
    if (!window.confirm(`Delete "${hook.name}"?\n\nPending deliveries will be cancelled.`)) return;
    try {
      await api.delete(`/webhooks/${hook.id}`);
      setHooks(h => h.filter(x => x.id !== hook.id));
      toast.success("Webhook deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="webhooks-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Webhook className="w-6 h-6 text-blue-600" />
            Webhooks
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Send real-time <strong>payment.completed</strong> events to external URLs when a payment is verified.
          </p>
        </div>
        <button
          onClick={() => { setEditHook(null); setShowModal(true); }}
          data-testid="add-webhook-btn"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />Add Webhook
        </button>
      </div>

      {/* Info banner */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex gap-3">
        <AlertCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 space-y-1">
          <p>Every registered webhook receives a <strong>POST</strong> request with student + payment data when a payment status changes to <strong>completed</strong>.</p>
          <p>Use the <strong>Send</strong> button to test your endpoint before going live.
            Delivery is retried up to 5 times with exponential backoff on failure.</p>
        </div>
      </div>

      {/* Payload preview */}
      <details className="mb-6 bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
        <summary className="px-4 py-3 text-sm font-medium text-slate-600 cursor-pointer select-none hover:bg-slate-100 transition-colors">
          View example payload
        </summary>
        <pre className="px-4 pb-4 text-xs text-slate-600 font-mono overflow-x-auto">
{`{
  "event": "payment.completed",
  "event_id": "uuid-...",
  "fired_at": "2026-09-27T10:30:00+00:00",
  "data": {
    "registration_id": "GDIOT-DRONE-OFF-WORK-26-007",
    "student_name": "Ninad Borde",
    "student_first_name": "Ninad",
    "student_last_name": "Borde",
    "standard": "10",
    "division": "A",
    "payment_status": "completed",
    "payment_date": "27-09-2026",
    "payment_time": "02:30 PM IST",
    "amount": 3000,
    "payment_id": "GDIOT-PAY-20260927-000007",
    "payment_method": "QR Code",
    "mobile": "9876543210",
    "email": "parent@example.com",
    "parent_name": "Suresh Borde",
    "program": "2 Days Drone Workshop",
    "level": "Beginner",
    "batch": "Morning Batch",
    "school": "",
    "page_name": "GDIOT Drone Workshop Registration",
    "invoice_number": "INV-0000007",
    "completed_at": "2026-09-27T10:30:00+00:00",
    "completed_date": "27-09-2026",
    "completed_time": "10:30 AM IST"
  }
}`}
        </pre>
      </details>

      {/* Webhook list */}
      {loading && (
        <div className="space-y-3">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-24 bg-slate-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      )}

      {!loading && hooks.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400" data-testid="empty-webhooks">
          <Webhook className="w-12 h-12 mb-3 opacity-30" />
          <p className="text-base font-medium">No webhooks configured</p>
          <p className="text-sm mt-1">Add your first webhook to start sending real-time data</p>
          <button
            onClick={() => setShowModal(true)}
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />Add Webhook
          </button>
        </div>
      )}

      {!loading && hooks.length > 0 && (
        <div className="space-y-3" data-testid="webhooks-list">
          {hooks.map(hook => (
            <WebhookCard
              key={hook.id}
              hook={hook}
              onToggle={handleToggle}
              onEdit={() => { setEditHook(hook); setShowModal(true); }}
              onDelete={handleDelete}
              onViewLogs={setLogsHook}
              onViewSecret={setSecretHook}
            />
          ))}
        </div>
      )}

      {/* New secret reveal (shown once after creation) */}
      {newSecret && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="new-secret-modal">
            <div className="px-6 py-5 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <h2 className="text-lg font-bold text-slate-800">Webhook Created</h2>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{newSecret.name}</p>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                Save this signing secret now — it won't be shown in full again.
              </p>
              <label className="block text-xs font-medium text-slate-500 mb-1">Signing Secret</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono break-all text-slate-800">
                  {newSecret.secret}
                </code>
                <button
                  onClick={() => { navigator.clipboard.writeText(newSecret.secret); toast.success("Copied"); }}
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="px-6 pb-5">
              <button onClick={() => setNewSecret(null)} className="w-full px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <WebhookModal
          hook={editHook}
          onClose={() => { setShowModal(false); setEditHook(null); }}
          onSave={handleSave}
        />
      )}
      {logsHook && (
        <LogsDrawer
          hook={logsHook}
          onClose={() => setLogsHook(null)}
          onRetry={loadHooks}
        />
      )}
      {secretHook && (
        <SecretModal
          webhookId={secretHook.id}
          webhookName={secretHook.name}
          onClose={() => setSecretHook(null)}
        />
      )}
    </div>
  );
}
